import type React from 'react';
import { useCallback } from 'react';

import { useSourceCreateState, useSourceDialog } from '../SourceCreate.hooks';
import { downloadCertificate } from '../SourceCreate.service';
import type { AlertDeviceField } from '../SourceCreate.types';

/**
 * Manages all event handlers for the source form.
 *
 * Responsibilities:
 * - Category selection changes
 * - Region selection changes
 * - Cloud provider selection changes
 * - Device alert fields changes
 * - Authentication mode changes
 * - Certificate mode changes
 * - Certificate download and regeneration
 *
 * @returns Event handlers for form interactions
 */
export const useSourceFormHandlers = () => {
  const {
    selectedCloudSource,
    setCategories,
    setRegion: setRegions,
    setCloudProvider: setCloudProviders,
    setDeviceAlertFields
  } = useSourceCreateState();

  const { setAuthenticationMode, setNewCertificate, setRegenerateCertificate } =
    useSourceDialog();

  /**
   * Handles category selection changes.
   * Categories can be provided as either an array or comma-separated string.
   */
  const handleCategoryChange = useCallback(
    (value: string | string[] | unknown[]) => {
      const selectedCategories = Array.isArray(value)
        ? (value as string[])
        : (value as string).split(',');
      setCategories(selectedCategories);
    },
    [setCategories]
  );

  /**
   * Handles cloud provider selection changes (e.g., AWS, Azure, GCP).
   * Supports both array and comma-separated string input.
   */
  const handleCloudProviderChange = useCallback(
    (value: string | string[]) => {
      const selectedCloudProviders = Array.isArray(value)
        ? value
        : value.split(',');
      setCloudProviders(selectedCloudProviders);
    },
    [setCloudProviders]
  );

  /**
   * Handles region selection changes.
   * Supports both array and comma-separated string input.
   */
  const handleRegionChange = useCallback(
    (value: string | string[]) => {
      const selectedRegions = Array.isArray(value) ? value : value.split(',');
      setRegions(selectedRegions);
    },
    [setRegions]
  );

  /**
   * Handles device alert fields selection for sources like Medigate.
   */
  const handleDeviceAlertFieldsChange = useCallback(
    (value: AlertDeviceField[]) => {
      const selectedAlertFields = Array.isArray(value) ? value : [];
      setDeviceAlertFields(selectedAlertFields);
    },
    [setDeviceAlertFields]
  );

  /**
   * Handles authentication mode changes (client_secret vs certificate).
   * Used for Microsoft source authentication configuration.
   */
  const handleAuthenticationModeChange = useCallback(
    (_event: React.SyntheticEvent, authMode: string) => {
      setAuthenticationMode(authMode);
    },
    [setAuthenticationMode]
  );

  /**
   * Handles certificate mode selection (existing vs new certificate).
   * Used when certificate authentication is selected.
   */
  const handleCertificationModeChange = useCallback(
    (value: string) => {
      setNewCertificate(value === 'new');
    },
    [setNewCertificate]
  );

  /**
   * Downloads the certificate file for the selected cloud source.
   */
  const downladCertificate = useCallback(() => {
    if (selectedCloudSource?.source_uid) {
      downloadCertificate(selectedCloudSource.source_uid);
    }
  }, [selectedCloudSource]);

  /**
   * Triggers certificate regeneration for the current source.
   */
  const regenerate = useCallback(() => {
    setRegenerateCertificate(true);
  }, [setRegenerateCertificate]);

  return {
    handleCategoryChange,
    handleRegionChange,
    handleCloudProviderChange,
    handleDeviceAlertFieldsChange,
    handleAuthenticationModeChange,
    handleCertificationModeChange,
    downladCertificate,
    regenerate
  };
};
